import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { contactsApi, type ContactRequest } from '../api/contacts'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import {
  FormGrid,
  Button,
  ButtonLink,
  Callout,
  Card,
  CardContent,
  CardHeader,
  LoadingState,
  Page as PageShell,
  PageHeader,
  Select,
  TextField,
} from '../components/ui'

/**
 * TASK-0171: minimal EDIT-only page for a `Contact`, reachable exclusively via
 * `/contacts/:id/edit` — the destination of someone else's duplicate-conflict resolution
 * (`CustomerFormPage.tsx`/`CompanyFormPage.tsx`'s "Usa questo record esistente" button when the
 * matched candidate is a `Contact`), or a direct URL. Deliberately no `mode` prop, no
 * create/list routes, no duplicate-conflict UI of its own: `ContactsController.Update` never runs
 * a duplicate check, so this page can never hit its own 409-from-duplicate the way
 * Customer/Company create can.
 */

interface FormState {
  firstName: string
  lastName: string
  email: string
  phone: string
  fiscalCode: string
  province: string
  source: string
  assignedAgentId: string
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  fiscalCode: '',
  province: '',
  source: '',
  assignedAgentId: '',
}

export function ContactFormPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = parseInt(idParam ?? '0', 10)

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [agents, setAgents] = useState<DirectoryUser[]>([])

  // Same justification as CompanyFormPage.tsx: AssignedAgentId names the EqualAgente-role user
  // handling this contact, so the selector is filtered server-side to that role.
  useEffect(() => {
    userDirectoryApi.list({ roles: ['EqualAgente'], activeOnly: false, limit: 200 })
      .then(setAgents)
      .catch(() => setAgents([]))
  }, [])

  useEffect(() => {
    setLoadingData(true)
    contactsApi
      .get(id)
      .then(contact => {
        setForm({
          firstName: contact.firstName ?? '',
          lastName: contact.lastName ?? '',
          email: contact.email ?? '',
          phone: contact.phone ?? '',
          fiscalCode: contact.fiscalCode ?? '',
          province: contact.province ?? '',
          source: contact.source ?? '',
          assignedAgentId: contact.assignedAgentId != null ? String(contact.assignedAgentId) : '',
        })
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento'))
      .finally(() => setLoadingData(false))
  }, [id])

  function setField(
    key: keyof FormState,
  ): (e: React.ChangeEvent<HTMLInputElement>) => void {
    return e => setForm(f => ({ ...f, [key]: e.target.value }))
  }

  async function handleSave(): Promise<void> {
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const body: ContactRequest = { firstName: form.firstName || 'Contatto' }
      if (form.lastName) body.lastName = form.lastName
      if (form.email) body.email = form.email
      if (form.phone) body.phone = form.phone
      if (form.fiscalCode) body.fiscalCode = form.fiscalCode
      if (form.province) body.province = form.province
      if (form.source) body.source = form.source
      if (form.assignedAgentId) body.assignedAgentId = Number(form.assignedAgentId)

      await contactsApi.update(id, body)
      setSaved(true)
    } catch (err) {
      // Includes the 409 "già unito" case (ContactsController.Update, contact.IsMerged) — the
      // shared api() client already turns it into a readable Error, shown here like any other
      // save failure. No special "already merged" UI (no canonical link, no redirect) — out of
      // scope per TASK-0171.
      setError(err instanceof Error ? err.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (loadingData) {
    return <LoadingState />
  }

  return (
    <PageShell>
      <PageHeader icon="person" back={{ to: '/customers', label: 'Clienti' }} title="Modifica contatto" />

      {saved && !error && (
        <Callout tone="success">Salvato.</Callout>
      )}
      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined">
        <CardHeader title="Informazioni anagrafiche" />
        <CardContent>
          <FormGrid>
            <div>
              <TextField
                label="Nome *"
                id="ctf-firstName"
                type="text"
                required
                value={form.firstName}
                onChange={setField('firstName')}
              />
            </div>
            <div>
              <TextField
                label="Cognome"
                id="ctf-lastName"
                type="text"
                value={form.lastName}
                onChange={setField('lastName')}
              />
            </div>
          </FormGrid>

          <FormGrid columns={3} className="mt-3">
            <div>
              <TextField
                label="Email"
                id="ctf-email"
                type="email"
                value={form.email}
                onChange={setField('email')}
              />
            </div>
            <div>
              <TextField
                label="Telefono"
                id="ctf-phone"
                type="text"
                value={form.phone}
                onChange={setField('phone')}
              />
            </div>
            <div>
              <Select
                label="Agente assegnato"
                id="ctf-assignedAgentId"
                value={form.assignedAgentId}
                onChange={e => setForm(f => ({ ...f, assignedAgentId: e.target.value }))}
              >
                <option value="">Nessun agente</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </Select>
            </div>
          </FormGrid>

          <FormGrid columns={3} className="mt-3">
            <div>
              <TextField
                label="Codice Fiscale"
                id="ctf-fiscalCode"
                type="text"
                value={form.fiscalCode}
                onChange={setField('fiscalCode')}
              />
            </div>
            <div>
              <TextField
                label="Provincia"
                id="ctf-province"
                type="text"
                maxLength={2}
                value={form.province}
                onChange={e => setForm(f => ({ ...f, province: e.target.value.toUpperCase() }))}
              />
            </div>
            <div>
              <TextField
                label="Origine"
                id="ctf-source"
                type="text"
                value={form.source}
                onChange={setField('source')}
              />
            </div>
          </FormGrid>
        </CardContent>
      </Card>

      <div className="d-flex gap-2">
        <Button
          variant="filled"
          icon="save"
          loading={saving}
          disabled={saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
        <ButtonLink to="/dashboard" variant="text">
          Chiudi
        </ButtonLink>
      </div>
    </PageShell>
  )
}
